import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Icon, type IconName } from './icon';

export interface BadgeProps {
  children: ReactNode;
  /** `accent` follows the module colour; use it for identity, never for status. */
  tone?: 'neutral' | 'brand' | 'accent';
  icon?: IconName;
  className?: string;
}

/** Label or count. Round, without border; status uses `StatusBadge`. */
export function Badge({ children, tone = 'neutral', icon, className }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex h-5 shrink-0 items-center gap-1 rounded-full px-2 text-xs font-medium whitespace-nowrap tabular',
        tone === 'neutral' && 'bg-sunken text-fg-muted',
        tone === 'brand' && 'bg-brand-subtle text-fg',
        tone === 'accent' && 'bg-accent-subtle text-fg',
        className,
      )}
    >
      {icon && <Icon name={icon} className="size-3" />}
      {children}
    </span>
  );
}

export type Status = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const statusIcons: Record<Status, IconName> = {
  success: 'circle-check',
  warning: 'triangle-alert',
  danger: 'circle-x',
  info: 'info',
  neutral: 'minus',
};

const statusClasses: Record<Status, string> = {
  success: 'border-success-line bg-success-subtle text-success',
  warning: 'border-warning-line bg-warning-subtle text-warning',
  danger: 'border-danger-line bg-danger-subtle text-danger',
  info: 'border-info-line bg-info-subtle text-info',
  neutral: 'border-line bg-sunken text-fg-muted',
};

export interface StatusBadgeProps {
  status: Status;
  children: ReactNode;
  icon?: IconName;
  className?: string;
}

/**
 * Semantic status with icon and text, never colour alone (WCAG 1.4.1). Square corners and a
 * border, so it does not look like a module accent (docs/ui/design-system.md).
 */
export function StatusBadge({ status, children, icon, className }: StatusBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex h-6 shrink-0 items-center gap-1.5 rounded-sm border px-1.5 text-xs font-medium whitespace-nowrap',
        statusClasses[status],
        className,
      )}
    >
      <Icon name={icon ?? statusIcons[status]} className="size-3.5" />
      {children}
    </span>
  );
}
